import JSZip from "jszip";

// Caminhos comuns de thumbnail em arquivos .3mf (Bambu, Orca, Prusa, etc.)
const THUMBNAIL_PATHS = [
  "Thumbnails/thumbnail.png",
  "Thumbnails/thumbnail_167x125.png",
  "Thumbnails/thumbnail_320x320.png",
  "Thumbnails/thumbnail_300x300.png",
  "3D/thumbnail.png",
  "Metadata/thumbnail.png",
];

// Carrega o blob numa <img> e desenha num <canvas> já redimensionado —
// base compartilhada por resizeToBase64 (retorna dataURL) e resizeParaBlob
// (retorna Blob, pra subir direto no Storage sem passar por base64).
function desenharCanvasRedimensionado(blob, maxW) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(blob);
    img.onload = () => {
      const ratio = Math.min(1, maxW / img.width);
      const w = Math.round(img.width * ratio);
      const h = Math.round(img.height * ratio);
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext("2d");
      // Sem isso o navegador usa reamostragem "low" por padrão, que borra bastante
      // quando reduz foto de celular (4000px+) pra poucas centenas de pixels.
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, 0, 0, w, h);
      URL.revokeObjectURL(url);
      resolve(canvas);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Falha ao carregar imagem")); };
    img.src = url;
  });
}

export function resizeToBase64(blob, maxW = 400) {
  return desenharCanvasRedimensionado(blob, maxW)
    .then(canvas => canvas.toDataURL("image/jpeg", 0.92))
    .catch(() => null);
}

// Só mede width/height de verdade (sem redesenhar em canvas) — usado pra
// escolher, entre vários thumbnails candidatos de um .3mf, qual é o de maior
// resolução de verdade (não só o que apareceu primeiro numa lista de nomes).
function medirImagem(blob) {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(blob);
    img.onload = () => { URL.revokeObjectURL(url); resolve({ width: img.width, height: img.height }); };
    img.onerror = () => { URL.revokeObjectURL(url); resolve({ width: 0, height: 0 }); };
    img.src = url;
  });
}

// Redimensiona pra um Blob JPEG (em vez de dataURL) — usado pras fotos de
// produto, que vão pro Firebase Storage e não pro Firestore. maxW maior que
// o do resizeToBase64 porque essas fotos são vistas em tela cheia no zoom.
export function resizeParaBlob(blob, maxW = 1920, quality = 0.94) {
  return desenharCanvasRedimensionado(blob, maxW)
    .then(canvas => new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality)))
    .catch(() => null);
}

// maxW bem maior que o padrão de resizeToBase64 (400) — o thumbnail embutido
// no .3mf normalmente já é pequeno por natureza (slicers costumam gerar algo
// entre 170px e 512px), então não faz sentido cortar ainda mais na extração;
// isso só pioraria uma imagem que já não é grande pra começo de conversa.
export async function extrairFoto3mf(file, maxW = 1200) {
  try {
    const zip = await JSZip.loadAsync(file);

    // Reúne TODOS os thumbnails candidatos do arquivo (caminhos conhecidos +
    // qualquer PNG com "thumbnail" no nome) e usa o de maior resolução de
    // verdade — o .3mf costuma ter mais de um tamanho embutido, e pegar
    // sempre o 1º da lista às vezes escolhia um bem pequeno (ex.: 167x125)
    // só por ordem, mesmo tendo uma versão maior disponível no mesmo arquivo.
    const candidatos = new Set(THUMBNAIL_PATHS.filter(p => zip.files[p] && !zip.files[p].dir));
    for (const filename of Object.keys(zip.files)) {
      const lower = filename.toLowerCase();
      if (lower.includes("thumbnail") && lower.endsWith(".png") && !zip.files[filename].dir) candidatos.add(filename);
    }
    if (candidatos.size === 0) {
      // último recurso: primeiro PNG do ZIP
      const primeiro = Object.keys(zip.files).find(f => f.toLowerCase().endsWith(".png") && !zip.files[f].dir);
      if (primeiro) candidatos.add(primeiro);
    }
    if (candidatos.size === 0) return null;

    let melhorBlob = null, melhorArea = -1;
    for (const path of candidatos) {
      const blob = await zip.files[path].async("blob");
      const { width, height } = await medirImagem(blob);
      const area = width * height;
      if (area > melhorArea) { melhorArea = area; melhorBlob = blob; }
    }
    return melhorBlob ? resizeToBase64(melhorBlob, maxW) : null;
  } catch {
    return null;
  }
}

// Extrai as "placas" (plates) de um projeto .3mf multi-placa (Bambu Studio/
// OrcaSlicer) — cada peça do projeto vira {nome, thumbnail}, usando a própria
// imagem de preview que o slicer já salva dentro do arquivo. Só leitura de
// metadados, nada de fatiar de verdade (ver memória "calculadora-slicing-
// automatico"). Retorna null quando o .3mf não é um projeto multi-placa (STL/
// OBJ convertido, ou projeto de peça única) — nesses casos o card usa o fluxo
// de sempre (1 arquivo = 1 linha).
export async function extrairPlacas3mf(file, maxW = 180) {
  try {
    const zip = await JSZip.loadAsync(file);
    const parser = new DOMParser();

    const lerXml = async (path) => {
      const entry = zip.files[path];
      if (!entry || entry.dir) return null;
      const doc = parser.parseFromString(await entry.async("text"), "application/xml");
      return doc.querySelector("parsererror") ? null : doc;
    };
    // Busca manual nos filhos DIRETOS (em vez de `:scope > metadata[...]`) —
    // além de evitar que um <object> com <part> aninhado (que também tem
    // <metadata key="name">) pegue o nome da part em vez do nome do objeto,
    // o seletor ":scope" quebra em alguns documents XML (testado contra .3mf
    // reais: gera um seletor inválido tipo "object,,, > metadata[...]").
    const metaValor = (el, chave) => {
      for (const filho of el.children) {
        if (filho.tagName === "metadata" && filho.getAttribute("key") === chave) return filho.getAttribute("value");
      }
      return null;
    };
    const semExtensao = (nome) => (nome || "").replace(/\.[a-z0-9]+$/i, "");

    let placas = [];

    // 1ª tentativa: model_settings.config — todo projeto Bambu/Orca tem esse
    // arquivo (não depende de já ter sido fatiado) e ele mapeia direto placa
    // -> arquivo de thumbnail e placa -> objetos usados nela.
    const modelSettings = await lerXml("Metadata/model_settings.config");
    if (modelSettings) {
      const nomesPorObjeto = new Map();
      modelSettings.querySelectorAll("object").forEach(obj => {
        const id = obj.getAttribute("id");
        const nome = metaValor(obj, "name");
        if (id && nome) nomesPorObjeto.set(id, semExtensao(nome));
      });
      modelSettings.querySelectorAll("plate").forEach((plate, i) => {
        const objetos = [...plate.querySelectorAll("model_instance")]
          .map(inst => nomesPorObjeto.get(metaValor(inst, "object_id")))
          .filter(Boolean);
        placas.push({
          indice: Number(metaValor(plate, "plater_id")) || i + 1,
          nomePersonalizado: metaValor(plate, "plater_name") || "",
          objetos: [...new Set(objetos)],
          thumbPath: metaValor(plate, "thumbnail_file") || metaValor(plate, "top_file"),
        });
      });
    }

    // 2ª tentativa (fallback): slice_info.config — só existe depois de um
    // fatiamento de verdade, mas cobre o caso do model_settings vir sem <plate>.
    if (placas.length === 0) {
      const sliceInfo = await lerXml("Metadata/slice_info.config");
      if (sliceInfo) {
        sliceInfo.querySelectorAll("plate").forEach((plate, i) => {
          const indice = Number(metaValor(plate, "index")) || i + 1;
          const objetos = [...plate.querySelectorAll("object")].map(o => semExtensao(o.getAttribute("name"))).filter(Boolean);
          placas.push({ indice, nomePersonalizado: "", objetos: [...new Set(objetos)], thumbPath: `Metadata/plate_${indice}.png` });
        });
      }
    }

    if (placas.length < 2) return null; // não é multi-placa — segue o fluxo atual

    const resultado = [];
    for (const p of placas) {
      const entry = p.thumbPath && zip.files[p.thumbPath] && !zip.files[p.thumbPath].dir ? zip.files[p.thumbPath] : null;
      const thumbnail = entry ? await resizeToBase64(await entry.async("blob"), maxW) : null;
      const nome = p.nomePersonalizado || p.objetos[0] || `Placa ${p.indice}`;
      resultado.push({ indice: p.indice, nome: p.objetos.length > 1 ? `${nome} +${p.objetos.length - 1}` : nome, thumbnail });
    }
    return resultado.sort((a, b) => a.indice - b.indice);
  } catch {
    return null;
  }
}

export async function imagemParaBase64(file, maxW = 400) {
  const blob = new Blob([await file.arrayBuffer()], { type: file.type });
  return resizeToBase64(blob, maxW);
}
